import React from "react";
import { useTranslation } from "react-i18next";
import { Maturity } from "../../../../types";
import { YesNoRadioButtons } from "../../../common/radio-buttons/radio-buttons";
import FullPicker from "../../../common/picker/full-picker";
import RadioButtons from "../../../common/radio-buttons/radio-buttons";
import { ModeFlags } from "../../../../hooks/game-settings/useGameSettings";

export interface PlayerSettings {
  maxPlayers: number;
  numCharactersSubmit: number;
  numCharactersSelect: number;    // Number of characters players must select
  characterMaturity: boolean;
}

interface PlayerSettingsProps {
  settings: PlayerSettings;
  onChange: (settings: Partial<PlayerSettings>) => void;
  modeFlags: ModeFlags;
  maturity: Maturity;
}

const PlayerSettings: React.FC<PlayerSettingsProps> = ({
  settings,
  onChange,
  modeFlags,
  maturity,
}) => {
  const { t } = useTranslation(["game-settings", "common"]);

  const isSolo = !!modeFlags.solo_writing;
  const isOtpMode = !!modeFlags.otp_mode;

  const handleNumCharacters = (num: number) => {
    onChange({ numCharactersSelect: num });
  };

  return (
    <>
      {!isSolo && (
        <YesNoRadioButtons
          label={t("body.charMaturity")}
          disabled={maturity === Maturity.ALL_AGES}
          yesLabel="yes"
          noLabel="no"
          subLabel="for your comfort levels"
          value={settings.characterMaturity}
          onSelect={(value) => onChange({ characterMaturity: value })}
        />
      )}

      {!isSolo && (
        <FullPicker
          label={t("body.maxPlayers")}
          value={settings.maxPlayers}
          onSelect={(value) => onChange({ maxPlayers: value })}
          options={[
            { value: 3 },
            { value: 6 },
            { value: 9 },
            { value: 12 },
            { value: 16 },
            { value: 12, isCustom: true },
          ]}
        />
      )}

      <RadioButtons
        label={t("body.numCharactersSelect")}
        value={settings.numCharactersSelect}
        options={[{ value: 2 }, { value: 3 }]}
        onSelect={(val) => handleNumCharacters(parseInt(val))}
        subLabel={t("body.numCharactersSelect2")}
      />

      {/* Hide Characters to Submit in OTP/Solo mode - players use all their characters */}
      {!isOtpMode && !isSolo && (
        <FullPicker
          label={t("body.numCharactersSubmit", "Characters to Submit")}
          subLabel={t(
            "body.numCharactersSubmit2",
            "How many characters should players submit to the pool?"
          )}
          value={settings.numCharactersSubmit}
          onSelect={(value) => onChange({ numCharactersSubmit: value })}
          options={[
            { value: 2, labels: ["2 Characters"] },
            { value: 3, labels: ["3 Characters"] },
            { value: 4, labels: ["4 Characters"] },
            { value: 5, labels: ["5 Characters"] },
            { value: 6, labels: ["6 Characters"] },
            { value: 7, labels: ["7 Characters"] },
            { value: 8, labels: ["8 Characters"] },
            { value: 5, isCustom: true },
          ]}
        />
      )}
    </>
  );
};

export default PlayerSettings;
export type { PlayerSettings, };
